/**
 * PublishControl — the publish gate's creator-facing act, on the hub's
 * Overview tab: the FULL surface for {@link usePublishAction} (the one publish
 * machine). It renders the whole story — the visibility state in prose, the
 * pending-audit explainer, the audit-failure detail — while the studio card's
 * corner button renders the same machine compactly and links here for detail.
 *
 * This component owns COPY and LAYOUT only. Every decision (which action is
 * offered, when pending resolves, how the audit stamp is watched) lives in the
 * hook, so the two surfaces can never disagree.
 */
import type { Creation } from '@djgg/creations';
import { PUBLISH_STATE_LABEL } from './publishState.js';
import { usePublishAction, type PublishActionKind } from './usePublishAction.js';

/** The hub speaks in full sentences (the card says "Publish"/"Retry"). */
const ACTION_LABEL: Record<PublishActionKind, string> = {
  publish: 'Publish to the gallery',
  'publish-now': 'Publish now',
  'retry-publish': 'Retry publish',
  unpublish: 'Unpublish',
};

export function PublishControl({ creation }: { creation: Creation & { id: string } }) {
  const { state, phase, busy, stamped, action, run, attributionModal, titleWarning } =
    usePublishAction(creation);

  return (
    <section className="overview-section" data-testid="publish-control">
      <h3 className="overview-section__title">Publishing</h3>
      <p className="muted overview-note" data-testid="publish-state" data-state={state} style={{ marginTop: 0 }}>
        <strong>{PUBLISH_STATE_LABEL[state]}</strong>
        {state === 'published' && (
          <>
            {' '}— listed in the gallery
            {creation.publishedAt
              ? ` (first published ${new Date(creation.publishedAt).toLocaleDateString()})`
              : ''}
            .
          </>
        )}
        {state === 'unpublished' && (
          <> — not listed in the gallery; anyone with the link can play.</>
        )}
        {state === 'private-fork' && (
          <>
            {' '}— visible only to its members until the first shipped change. Ship a change from
            the Iterate tab to unlock publishing.
          </>
        )}
      </p>

      {phase.kind === 'pending' && (
        <p className="pill" data-testid="publish-pending">
          {stamped && phase.autoRetried
            ? 'The playtest finished — publish when ready.'
            : 'We’re test-playing your game — every game gets a quick automated playtest before it goes public. This usually takes a few minutes; it publishes on its own when the playtest lands.'}
        </p>
      )}
      {phase.kind === 'stale-audit' && (
        <p className="pill" data-testid="publish-stale-audit">
          The playtest finished, but a newer build went live while it ran — publish now to
          test-play and publish the current version.
        </p>
      )}
      {phase.kind === 'audit-failed' && (
        <p className="error-text" data-testid="publish-audit-failed">
          The playtest audit failed — the game couldn’t be test-played to completion
          {phase.message ? ` (${phase.message})` : ''}. Iterate on the game and retry, or retry
          publishing to run a fresh audit.
        </p>
      )}
      {phase.kind === 'error' && (
        <p className="error-text" data-testid="publish-error">
          {phase.message}
        </p>
      )}

      {titleWarning && (
        <p className="pill" data-testid="publish-title-warning">
          {titleWarning}
        </p>
      )}

      {action && (
        <button
          className={`btn btn--sm${action === 'unpublish' ? '' : ' btn--primary'}`}
          data-testid="publish-action"
          onClick={run}
          disabled={busy}
        >
          {busy ? 'Working…' : ACTION_LABEL[action]}
        </button>
      )}

      {attributionModal}
    </section>
  );
}
